export const GET_EVENT_DETAILS = 'eventDetails/GET_EVENT_DETAILS';
export const GET_EVENT_DETAILS_START = `${GET_EVENT_DETAILS}_START`;
export const GET_EVENT_DETAILS_SUCCESS = `${GET_EVENT_DETAILS}_SUCCESS`;
export const GET_EVENT_DETAILS_FAIL = `${GET_EVENT_DETAILS}_FAIL`;

export const GET_EVENT_DETAILS_REFETCH = 'eventDetails/GET_EVENT_DETAILS_AUTO';
export const GET_EVENT_DETAILS_REFETCH_START = `${GET_EVENT_DETAILS_REFETCH}_START`;
export const GET_EVENT_DETAILS_REFETCH_SUCCESS = `${GET_EVENT_DETAILS_REFETCH}_SUCCESS`;
export const GET_EVENT_DETAILS_REFETCH_FAIL = `${GET_EVENT_DETAILS_REFETCH}_FAIL`;

export const INIT_EVENT_CREATION_SUCCESS = 'INIT_EVENT_CREATION_SUCCESS';
export const RESET_NEWLY_CREATED = 'RESET_NEWLY_CREATED';

export function getEvent({
  eventId,
  params,
  uri = '/webapi/xm/1/events',
  refetch = false,
}) {
  return {
    type: refetch ? GET_EVENT_DETAILS_REFETCH : GET_EVENT_DETAILS,
    meta: {
      xmApi: true,
      method: 'get',
      uri: `${uri}/${eventId}`,
      params: {
        ...params,
        embed: [
          'messages',
          'properties',
          'recipients',
          'responseOptions',
          'responseOptions.translations',
          ...(params?.embed ?? []),
        ].join(','),
      },
    },
  };
}

export const GET_PERMITTED_USERS = 'eventDetails/GET_PERMITTED_USERS';
export const GET_PERMITTED_USERS_START = `${GET_PERMITTED_USERS}_START`;
export const GET_PERMITTED_USERS_SUCCESS = `${GET_PERMITTED_USERS}_SUCCESS`;

export const getPermittedUsers = userList => {
  return {
    type: GET_PERMITTED_USERS,
    meta: {
      xmApi: true,
      method: 'get',
      uri: '/webapi/xm/1/people',
      params: {
        id: userList.join(',') || undefined,
        embed: 'permittedActions',
      },
    },
  };
};

const getUsers = ({ dispatch, data, total, limit }) => {
  let start = 0;
  let end = limit;
  const promises = [];
  do {
    promises.push(
      dispatch(getPermittedUsers(data.slice(start, end).map(user => user.id))),
    );
    start = end;
    end += limit;
  } while (start < total);

  // When there are too many users, the request header might get too big and 431.
  // getUsers will try to run again with less users per request until limit < 10
  return Promise.all(promises).catch(err => {
    if (err.code !== 431 || limit < 10) {
      throw err;
    }

    return getUsers({ dispatch, data, total, limit: Math.floor(limit / 2) });
  });
};

export const GET_RECIPIENTS = 'eventDetails/GET_RECIPIENTS';
export const GET_RECIPIENTS_START = `${GET_RECIPIENTS}_START`;
export const GET_RECIPIENTS_SUCCESS = `${GET_RECIPIENTS}_SUCCESS`;
export const GET_RECIPIENTS_FAIL = `${GET_RECIPIENTS}_FAIL`;

export function overrideDefaultEncodingFunc(uri, params) {
  if (params) {
    if (params.response) {
      const { response } = params;
      let newParams = Object.keys(params)
        .filter(key => params[key] !== undefined)
        .map(
          param =>
            `${encodeURIComponent(param)}=${encodeURIComponent(params[param])}`,
        )
        .join('&');
      const encodedParams = newParams.split('&');
      newParams = encodedParams
        .map(param => {
          if (param.includes('response=')) {
            return `response=${response}`;
          }
          return param;
        })
        .join('&');
      return `${uri}?${newParams}`;
    }
    return `${uri}?${Object.keys(params)
      .filter(key => params[key] !== undefined)
      .map(
        param =>
          `${encodeURIComponent(param)}=${encodeURIComponent(params[param])}`,
      )
      .join('&')}`;
  }
  return uri;
}

export const getEventRecipients =
  ({ eventId, params, personNotificationLimit, uri = '/webapi/xm/1/events' }) =>
  dispatch => {
    return dispatch({
      type: GET_RECIPIENTS,
      payload: {
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
      },
      meta: {
        xmApi: true,
        method: 'get',
        uri: `${uri}/${eventId}/person-notifications`,
        customUriWithParamsGenerator: overrideDefaultEncodingFunc,
        params: {
          ...params,
          embed: 'properties,site',
          limit: personNotificationLimit,
        },
      },
    })
      .then(res => {
        if (res.payload?.jobId && res.payload?.status === 'RUNNING') {
          return res;
        }

        const { data } = res.payload;
        const limit = 100;
        const total = data.length;

        getUsers({ dispatch, data, total, limit });
        return res;
      })
      .catch(err => {
        dispatch({ type: GET_RECIPIENTS_FAIL, payload: err });
      });
  };

export const GET_MORE_RECIPIENTS = 'eventDetails/GET_MORE_RECIPIENTS';
export const GET_MORE_RECIPIENTS_START = `${GET_MORE_RECIPIENTS}_START`;
export const GET_MORE_RECIPIENTS_SUCCESS = `${GET_MORE_RECIPIENTS}_SUCCESS`;
export const GET_MORE_RECIPIENTS_FAIL = `${GET_MORE_RECIPIENTS}_FAIL`;

export const getMore = ({ uri }) => ({
  type: GET_MORE_RECIPIENTS,
  payload: {},
  meta: {
    xmApi: true,
    method: 'get',
    uri,
    customUriWithParamsGenerator: x => x,
  },
});

export const GET_ALL_RECIPIENTS = 'eventDetails/GET_ALL_RECIPIENTS';
export const GET_ALL_RECIPIENTS_START = `${GET_ALL_RECIPIENTS}_START`;
export const GET_ALL_RECIPIENTS_SUCCESS = `${GET_ALL_RECIPIENTS}_SUCCESS`;
export const GET_ALL_RECIPIENTS_FAIL = `${GET_ALL_RECIPIENTS}_FAIL`;

export const getAllRecipients = (eventId, params, uri) => dispatch => {
  dispatch({
    type: GET_ALL_RECIPIENTS,
    payload: {
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
    },
    meta: {
      xmApi: true,
      method: 'getall',
      uri: `${uri}/${eventId}/person-notifications`,
      params: {
        limit: 1000,
        ...params,
      },
    },
  })
    .then(res => {
      const { total, data } = res.payload;
      const limit = 100;

      return getUsers({ dispatch, data, total, limit });
    })
    .then(() => {
      dispatch({ type: GET_ALL_RECIPIENTS_SUCCESS });
    })
    .catch(err => {
      dispatch({ type: GET_ALL_RECIPIENTS_FAIL, payload: err });
    });
};

export const CLEAR_DETAILS = 'eventDetails/CLEAR_DETAILS';
export const clearDetails = () => ({
  type: CLEAR_DETAILS,
});

export const CLEAR_RECIPIENTS = 'eventDetails/CLEAR_RECIPIENTS';
export const clearRecipients = () => ({
  type: CLEAR_RECIPIENTS,
});

export const resetNewlyCreated = () => dispatch => {
  dispatch({ type: RESET_NEWLY_CREATED });
};
